import { useQuery } from "@tanstack/react-query";
import type { Client } from "openapi-fetch";
import { AuditServiceSchemaTypes } from "@bfc-software/api-schemas";
import { useAssertedTenancyContext } from "@bfc-software/app-toolbox";
import { useAuditFetchClient } from "@bfc-software/bfc-web-ui/lib/audit-api-client";

import {
  TPS_EVENT_TYPE,
  toActivityEntriesFromEvents,
  type WireTaskPlannerEvent,
} from "./audit-wire";
import type { ActivityEntry, Task } from "./types";

/**
 * The task detail drawer's Activity timeline.
 *
 * Read from audit_service's `task-planner-events`, which keeps every event the
 * task planner emitted, rather than from `ListTaskHistory`'s `activity` (that
 * read still supplies the pick lines' execution state - see `useTaskDetail`).
 * Two lists, one answer:
 *
 * - the task's own events, by `task_activation_id`: assigned, claimed,
 *   completed, and one event per proof action on each of its picks;
 * - its plan's `TaskPlanActivated` events, by plan and event type, because TPS
 *   has no per-task activation event and "activated" is the plan's.
 *
 * Both are read in one query so the timeline has one loading state and one
 * failure: if either list cannot be had, the section says it is unavailable
 * rather than drawing a timeline with a hole where one of them should be.
 */

type AuditPaths = AuditServiceSchemaTypes.paths;

const PATH =
  "/v1/{orgExtId}/warehouses/{warehouseExtId}/task-planner-events" as const;

type ListQuery = NonNullable<
  AuditServiceSchemaTypes.operations["ListTaskPlannerEvents"]["parameters"]["query"]
>;

/** The service's own maximum page size. */
const PAGE_LIMIT = 200;

/**
 * A guard against a runaway cursor, not a size anyone is expected to reach: a
 * task's picks are bounded by its plan, so 25 full pages (5,000 events) is far
 * past any task a selector works in one go. Past it the read fails rather than
 * quietly drawing the newest 5,000 and calling that the whole task.
 */
const MAX_PAGES = 25;

/**
 * Every event matching `query`, newest first, following the cursor to the end.
 *
 * The service pages at 200 and a task with a few dozen picks emits more than
 * that, so one page would silently drop the oldest - the task's own start.
 */
async function listAllEvents(
  client: Client<AuditPaths>,
  tenancy: { orgId: string; warehouseId: string },
  query: Omit<ListQuery, "after" | "before" | "limit">,
): Promise<WireTaskPlannerEvent[]> {
  const events: WireTaskPlannerEvent[] = [];
  let after: string | undefined;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const { data, response } = await client.GET(PATH, {
      params: {
        path: {
          orgExtId: tenancy.orgId,
          warehouseExtId: tenancy.warehouseId,
        },
        query: { ...query, limit: PAGE_LIMIT, ...(after ? { after } : {}) },
      },
    });
    if (!response.ok || data === undefined) {
      throw new Error(`task-planner-events: ${response.status}`);
    }

    events.push(...data.items);
    if (!data.has_next || !data.next_cursor) return events;
    after = data.next_cursor;
  }

  throw new Error(`task-planner-events: more than ${MAX_PAGES} pages`);
}

/**
 * The task's Activity entries, oldest first. Disabled for a planned task: it
 * has no activation, so there is nothing to ask for (the caller treats that the
 * same as `useTaskDetail` treats `/history`).
 */
export function useTaskActivity(task: Task) {
  const tenancy = useAssertedTenancyContext();
  const client = useAuditFetchClient();
  const activated = task.kind === "activated" ? task : null;

  return useQuery({
    queryKey: [
      "task-activity",
      tenancy.orgId,
      tenancy.warehouseId,
      activated?.activationId ?? null,
      activated?.planActivationId ?? null,
    ],
    enabled: activated !== null,
    queryFn: async (): Promise<ActivityEntry[]> => {
      if (activated === null) return [];
      const [taskEvents, planEvents] = await Promise.all([
        listAllEvents(client, tenancy, {
          task_activation_id: activated.activationId,
        }),
        listAllEvents(client, tenancy, {
          task_plan_internal_id: activated.planInternalId,
          event_type: TPS_EVENT_TYPE.planActivated,
        }),
      ]);
      return toActivityEntriesFromEvents(activated, taskEvents, planEvents);
    },
  });
}
